// Adapted from davinci-elections apps/web/src/components/member/register/CodeEntry.tsx @ 6e23f58
import { Button, Field, Flex, Input, Stack, Text } from '@chakra-ui/react'
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { isApiError } from '~/api/client'
import { useT } from '~/i18n/use-t'
import { ActionBar } from './ActionBar'
import type { FaqId } from './faq-ids'
import { ErrorAlert, useFriendlyError } from './feedback'
import { FormStack } from './layout'
import { WhyLink } from './WhyLink'

const FORM_ID = 'code-form'
/** "Send a new email" of the sign-in pages waits this long after a sign-in email (the server allows 5 an hour). */
export const SIGN_IN_RESEND_MS = 30_000

/** Seconds left until `target` (epoch ms), ticking every second while > 0. */
export function useSecondsUntil(target: number): number {
  const [now, setNow] = useState(() => Date.now())
  const left = Math.max(0, Math.ceil((target - now) / 1000))
  const running = left > 0
  useEffect(() => setNow(Date.now()), [target])
  useEffect(() => {
    if (!running) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [running, target])
  return left
}

/** The words a code box may change (the sign-in email asks for "Send a new email" instead of "Send the code again"). */
export interface CodeEntryCopy {
  submit: string
  resend: string
  resent: string
  changeEmail: string
  expired: string
  tooMany: string
  /** 400 `invalid_challenge`: the code works only in the browser that asked for it (the sign-in email). */
  challenge: string
}

/**
 * Code entry for the email step (copy v9 S2): one wide numeric field that takes a paste, "Send the code again" with
 * its countdown, a way back to change the email and "No code?" (FAQ `code`). The notes live in the FAQ. The sign-in
 * pages reuse it with their own words (`copy`), their own FAQ entry (`faq`, null for none) and no "wrong email" link
 * when `onChangeEmail` is left out.
 */
export function CodeEntry({
  resendAt,
  onCheck,
  onResend,
  onChangeEmail,
  copy = {},
  faq = 'code',
  formId = FORM_ID,
}: {
  /** Epoch ms from which "Send the code again" is allowed. */
  resendAt: number
  onCheck: (code: string) => Promise<void>
  onResend: () => Promise<void>
  onChangeEmail?: () => void
  copy?: Partial<CodeEntryCopy>
  faq?: FaqId | null
  /** The form's id (the submit button sits in the action bar, `form={id}`): two code boxes never share one. */
  formId?: string
}) {
  const { t } = useT()
  const friendly = useFriendlyError()
  const [code, setCode] = useState('')
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [checking, setChecking] = useState(false)
  const [resending, setResending] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const wait = useSecondsUntil(resendAt)
  const codeRef = useRef<HTMLInputElement>(null)
  // A wrong or short code: the focus goes back to the field with the digits selected, ready to retype.
  const flagCode = (text: string) => {
    setFieldError(text)
    queueMicrotask(() => {
      codeRef.current?.focus()
      codeRef.current?.select()
    })
  }

  const wording = (err: unknown): string | null => {
    if (isApiError(err, 'invalid_code')) {
      const left = (err.details as { attemptsLeft?: number } | undefined)?.attemptsLeft
      return typeof left === 'number' && left > 0 ? t('email.errNotRightLeft', { count: left }) : t('email.errNotRight')
    }
    if (isApiError(err, 'code_expired')) return copy.expired ?? t('email.errExpired')
    if (isApiError(err, 'code_attempts_exceeded')) return copy.tooMany ?? t('email.errTooMany')
    if (copy.challenge && isApiError(err, 'invalid_challenge')) return copy.challenge
    return null
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setNotice(null)
    const c = code.replace(/\s/g, '')
    if (!/^\d{6}$/.test(c)) {
      flagCode(t('email.errEmpty'))
      return
    }
    setFieldError(null)
    setChecking(true)
    try {
      await onCheck(c)
    } catch (err) {
      const text = wording(err)
      if (text) flagCode(text)
      else setError(err)
    } finally {
      setChecking(false)
    }
  }

  const resend = async () => {
    setError(null)
    setFieldError(null)
    setNotice(null)
    setResending(true)
    try {
      await onResend()
      setCode('')
      setNotice(copy.resent ?? t('email.resent'))
    } catch (err) {
      setError(err)
    } finally {
      setResending(false)
    }
  }

  return (
    <Stack gap={5}>
      <FormStack id={formId} gap={4} onSubmit={submit} noValidate>
        <Field.Root invalid={Boolean(fieldError)}>
          <Field.Label>{t('email.code')}</Field.Label>
          <Input
            ref={codeRef}
            aria-invalid={Boolean(fieldError)}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^\d\s]/g, '').slice(0, 9))}
            autoComplete='one-time-code'
            inputMode='numeric'
            enterKeyHint='done'
            pattern='[0-9]*'
            maxLength={9}
            fontFamily='mono'
            fontSize='2xl'
            minH='14'
            letterSpacing='0.35em'
            textAlign='center'
          />
          <Field.ErrorText role='alert'>{fieldError}</Field.ErrorText>
        </Field.Root>
        {error != null && <ErrorAlert>{friendly(error)}</ErrorAlert>}
        {notice && <Text role='status'>{notice}</Text>}
        <ActionBar>
          <Button type='submit' form={formId} size='lg' w='full' loading={checking}>
            {copy.submit ?? t('email.continue')}
          </Button>
        </ActionBar>
      </FormStack>
      <Stack gap={1} align='flex-start'>
        {wait > 0 ? (
          <Text minH='12' display='flex' alignItems='center'>
            {t('email.resendWait', { seconds: wait })}
          </Text>
        ) : (
          <WhyLink
            label={copy.resend ?? t('email.resend')}
            color='fg'
            disabled={resending}
            aria-busy={resending}
            onClick={() => void resend()}
          />
        )}
        {/* One row when it fits (a 320 px phone included): the screen then needs no scrolling. */}
        <Flex wrap='wrap' columnGap={4}>
          {onChangeEmail && (
            <WhyLink label={copy.changeEmail ?? t('email.wrongEmail')} color='fg' onClick={onChangeEmail} />
          )}
          {faq && <WhyLink faq={faq} label={t('email.noCode')} />}
        </Flex>
      </Stack>
    </Stack>
  )
}
